import { Project } from '@applicator/shared'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
  defaultNewProject,
  defaultNewProjectBullet,
  normalizeProjectsForEditor,
  projectsNeedEditorNormalization,
} from '../../utils/projectEditor'
import { reorderItemsById } from './useWorkExperienceRoleHandlers/reorderHelpers'
import ProjectEditorItem from './ProjectEditorItem'
import ResumeSectionEmptyHint from './ResumeSectionEmptyHint'

interface ProjectsEditorProps {
  data: Project[]
  onChange: (data: Project[]) => void
  onAddProjectRef?: (fn: () => void) => void
}

export default function ProjectsEditor({ data, onChange, onAddProjectRef }: ProjectsEditorProps) {
  const projects = Array.isArray(data) ? data : []
  const [editingNameId, setEditingNameId] = useState<string | null>(null)
  const [editingCompanyId, setEditingCompanyId] = useState<string | null>(null)
  const [editingStartDateId, setEditingStartDateId] = useState<string | null>(null)
  const [editingEndDateId, setEditingEndDateId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [editingCompany, setEditingCompany] = useState('')
  const [editingStartDate, setEditingStartDate] = useState('')
  const [editingEndDate, setEditingEndDate] = useState('')
  const [editingBulletId, setEditingBulletId] = useState<string | null>(null)
  const [editingBulletText, setEditingBulletText] = useState('')
  const [hoveredBulletId, setHoveredBulletId] = useState<string | null>(null)
  const [draggedBulletId, setDraggedBulletId] = useState<string | null>(null)
  const [draggedProjectId, setDraggedProjectId] = useState<string | null>(null)
  const [dragOverBulletIndex, setDragOverBulletIndex] = useState<number | null>(null)
  const [dragOverProjectIndex, setDragOverProjectIndex] = useState<number | null>(null)
  const bulletTextareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (projectsNeedEditorNormalization(projects)) {
      onChange(normalizeProjectsForEditor(projects))
    }
  }, [projects, onChange])

  const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto'
    textarea.style.height = `${textarea.scrollHeight}px`
  }

  const updateProjects = useCallback(
    (updater: (current: Project[]) => Project[]) => {
      onChange(updater(projects))
    },
    [onChange, projects]
  )

  const handleAddProject = useCallback(() => {
    const newProject = defaultNewProject()
    onChange([...projects, newProject])
    setTimeout(() => {
      setEditingNameId(newProject.id)
      setEditingName('')
    }, 0)
  }, [onChange, projects])

  useEffect(() => {
    if (onAddProjectRef) {
      onAddProjectRef(handleAddProject)
    }
    return () => {
      if (onAddProjectRef) {
        onAddProjectRef(() => {})
      }
    }
  }, [handleAddProject, onAddProjectRef])

  const handleUpdateProjectField = (
    projectId: string,
    field: 'name' | 'company' | 'startDate' | 'endDate',
    value: string
  ) => {
    updateProjects((current) =>
      current.map((project) => (project.id === projectId ? { ...project, [field]: value } : project))
    )
  }

  const handleToggleProject = (projectId: string) => {
    updateProjects((current) =>
      current.map((project) =>
        project.id === projectId ? { ...project, included: !project.included } : project
      )
    )
  }

  const handleDeleteProject = (projectId: string) => {
    updateProjects((current) => current.filter((project) => project.id !== projectId))
  }

  const handleReorderProjects = (draggedId: string, targetIndex: number) => {
    updateProjects((current) => reorderItemsById(current, draggedId, targetIndex))
  }

  const updateProjectBullets = (
    projectId: string,
    updater: (bullets: NonNullable<Project['bulletPoints']>) => NonNullable<Project['bulletPoints']>
  ) => {
    updateProjects((current) =>
      current.map((project) => {
        if (project.id !== projectId) return project
        return { ...project, bulletPoints: updater(project.bulletPoints ?? []) }
      })
    )
  }

  const handleAddBullet = (projectId: string) => {
    const newBullet = defaultNewProjectBullet()
    updateProjectBullets(projectId, (bullets) => [...bullets, newBullet])
    setTimeout(() => {
      setEditingBulletId(newBullet.id)
      setEditingBulletText('')
    }, 0)
  }

  const handleToggleBullet = (projectId: string, bulletId: string) => {
    updateProjectBullets(projectId, (bullets) =>
      bullets.map((bullet) =>
        bullet.id === bulletId ? { ...bullet, included: !bullet.included } : bullet
      )
    )
  }

  const handleDeleteBullet = (projectId: string, bulletId: string) => {
    updateProjectBullets(projectId, (bullets) => bullets.filter((bullet) => bullet.id !== bulletId))
    if (editingBulletId === bulletId) {
      setEditingBulletId(null)
      setEditingBulletText('')
    }
  }

  const handleReorderBullets = (projectId: string, draggedId: string, targetIndex: number) => {
    updateProjectBullets(projectId, (bullets) => reorderItemsById(bullets, draggedId, targetIndex))
  }

  const handleSaveBullet = (projectId: string, bulletId: string) => {
    updateProjectBullets(projectId, (bullets) =>
      bullets.map((bullet) =>
        bullet.id === bulletId ? { ...bullet, text: editingBulletText } : bullet
      )
    )
    setEditingBulletId(null)
    setEditingBulletText('')
  }

  const handleBulletTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditingBulletText(e.target.value)
  }

  return (
    <div className="space-y-4">
      {projects.map((project, projectIndex) => (
        <ProjectEditorItem
          key={project.id}
          project={project}
          projectIndex={projectIndex}
          editingNameId={editingNameId}
          editingCompanyId={editingCompanyId}
          editingStartDateId={editingStartDateId}
          editingEndDateId={editingEndDateId}
          editingName={editingName}
          editingCompany={editingCompany}
          editingStartDate={editingStartDate}
          editingEndDate={editingEndDate}
          editingBulletId={editingBulletId}
          editingBulletText={editingBulletText}
          hoveredBulletId={hoveredBulletId}
          draggedBulletId={draggedBulletId}
          draggedProjectId={draggedProjectId}
          dragOverBulletIndex={dragOverBulletIndex}
          dragOverProjectIndex={dragOverProjectIndex}
          bulletTextareaRef={bulletTextareaRef}
          setEditingNameId={setEditingNameId}
          setEditingCompanyId={setEditingCompanyId}
          setEditingStartDateId={setEditingStartDateId}
          setEditingEndDateId={setEditingEndDateId}
          setEditingName={setEditingName}
          setEditingCompany={setEditingCompany}
          setEditingStartDate={setEditingStartDate}
          setEditingEndDate={setEditingEndDate}
          setEditingBulletId={setEditingBulletId}
          setEditingBulletText={setEditingBulletText}
          setHoveredBulletId={setHoveredBulletId}
          setDraggedBulletId={setDraggedBulletId}
          setDraggedProjectId={setDraggedProjectId}
          setDragOverBulletIndex={setDragOverBulletIndex}
          setDragOverProjectIndex={setDragOverProjectIndex}
          onUpdateProjectField={handleUpdateProjectField}
          onToggleProject={handleToggleProject}
          onDeleteProject={handleDeleteProject}
          onAddBullet={handleAddBullet}
          onToggleBullet={handleToggleBullet}
          onDeleteBullet={handleDeleteBullet}
          onReorderBullets={handleReorderBullets}
          onReorderProjects={handleReorderProjects}
          onSaveBullet={handleSaveBullet}
          onBulletTextChange={handleBulletTextChange}
          adjustTextareaHeight={adjustTextareaHeight}
        />
      ))}

      {projects.length === 0 && <ResumeSectionEmptyHint sectionType="projects" />}
    </div>
  )
}
